import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";

import { getAccessTokenCookie, setAuthCookies } from "../lib/cookie";
import { axiosClient } from "../contexts/axios/AxiosContext";
import { classifyFailure, clearReportedFailures, reportFailure } from "../lib/session-failure";

/**
 * How far ahead of expiry to refresh. The access token is read from a cookie by the backend, so the
 * window needs to comfortably cover a slow refresh round-trip plus any requests already in flight.
 */
const EAGER_EXPIRY_SECS = 2 * 60;
const RETRY_DELAY_SECS = 30;

export interface Auth0SessionRefresherProps {
  children?: React.ReactNode;
}

function getExpiryFromIdToken(idToken: string): number {
  const decoded = jwtDecode<{ exp?: number }>(idToken);
  if (!decoded.exp) {
    throw new Error("ID token has no expiry.");
  }

  return (decoded.exp * 1000 - Date.now()) / 1000;
}

function getExpiry(token: string): number | undefined {
  try {
    const exp = jwtDecode<{ exp?: unknown }>(token).exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether `candidate` expires later than `current`. A candidate without a
 * readable expiry never wins; a missing or unreadable `current` always loses.
 */
function isTokenNewer(candidate: string, current: string | undefined): boolean {
  const candidateExp = getExpiry(candidate);
  if (candidateExp === undefined) {
    return false;
  }

  const currentExp = current === undefined ? undefined : getExpiry(current);
  if (currentExp === undefined) {
    return true;
  }

  return candidateExp > currentExp;
}

export const Auth0SessionRefresher = ({ children }: Auth0SessionRefresherProps): JSX.Element => {
  const auth0 = useAuth0();

  const [error, setError] = useState<Error | null>(null);
  // eslint-disable-next-line react-hooks/purity -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const [online, setOnline] = useState(navigator.onLine);
  const onNetworkStatusChange = () => {
    setOnline(navigator.onLine);
  };

  const [documentVisible, setDocumentVisible] = useState(!document.hidden);
  const onDocumentVisibleChange = () => {
    setDocumentVisible(!document.hidden);
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("online", onNetworkStatusChange);
      window.addEventListener("offline", onNetworkStatusChange);
      document.addEventListener("visibilitychange", onDocumentVisibleChange);

      return () => {
        window.removeEventListener("online", onNetworkStatusChange);
        window.removeEventListener("offline", onNetworkStatusChange);
        document.removeEventListener("visibilitychange", onDocumentVisibleChange);
      };
    }
  }, []);

  const refreshing = useRef<Promise<void> | null>(null);

  /**
   * Force refresh the access token and ID token, updating the cookies.
   */
  const refreshTokens = useCallback(async () => {
    refreshing.current ??= (async () => {
      try {
        const tokens = await auth0.getAccessTokenSilently({
          detailedResponse: true,
          cacheMode: "off",
        });

        setAuthCookies({ accessToken: tokens.access_token });
        setError(null);
        clearReportedFailures();
      } catch (e: any) {
        setError(e);
      } finally {
        refreshing.current = null;
      }
    })();

    await refreshing.current;
  }, [auth0]);

  /**
   * Get the current tokens from the Auth0 cache without hitting the network.
   * Returns undefined if nothing is cached, null on error.
   */
  const getCachedTokens = useCallback(async () => {
    try {
      // With "cache-only" mode, getAccessTokenSilently returns undefined if no
      // tokens are in the cache. It might still be possible to fetch new tokens
      // though.
      return await auth0.getAccessTokenSilently({
        detailedResponse: true,
        cacheMode: "cache-only",
      });
    } catch (e: any) {
      setError(e);
      return null;
    }
  }, [auth0]);

  const ensureFreshCookie = useCallback(async () => {
    // A refresh that already failed leaves the cookie stale, so every later request would try again
    // and stall behind the same failing Auth0 call. Let requests fail fast to their 403 instead,
    // which is what sends the user to log in again. Recovery is the retry timer's, and for an
    // expected failure there is none: see the effect below.
    if (error) {
      return;
    }

    const token = getAccessTokenCookie();
    const exp = token ? getExpiry(token) : undefined;
    if (exp !== undefined && exp * 1000 - Date.now() <= EAGER_EXPIRY_SECS * 1000) {
      await refreshTokens();
    }
  }, [error, refreshTokens]);

  useEffect(() => {
    const interceptor = axiosClient.interceptors.request.use(async (config) => {
      await ensureFreshCookie();
      return config;
    });

    return () => axiosClient.interceptors.request.eject(interceptor);
  }, [ensureFreshCookie]);

  const refreshTimer = useCallback(async () => {
    if (online) {
      try {
        await refreshTokens();
        setLastRefresh(Date.now());
      } catch (err: any) {
        setError(err);
      }
    }
  }, [online, refreshTokens, setLastRefresh, setError]);

  useEffect(() => {
    if (error) {
      reportFailure("Session refresh failed", error);
    }

    if (auth0.error) {
      reportFailure("Session refresh failed with an Auth0 error", auth0.error);
      return;
    } else if (auth0.isLoading) {
      return;
    }

    // An expected failure means the SSO session itself is over, so no retry can recover it. Arming
    // the timer would call Auth0 every RETRY_DELAY_SECS for as long as the tab stays open - about
    // 2,880 times a day - and fail identically each time. Returning to the tab or regaining the
    // network re-runs this effect and is deliberately not a fresh attempt either.
    //
    // Requests still go out with the stale cookie and still fail to their 403, which is what
    // renders the session-expired page. Recovery is logging in again, not a retry.
    if (error && classifyFailure(error) === "info") {
      return;
    }

    const promise = (async () => {
      if (!online) {
        return;
      }

      const tokens = await getCachedTokens();
      if (tokens === null) {
        return;
      }

      let minExpiresIn = 0;
      if (tokens !== undefined) {
        // The Auth0 provider may have fetched fresh tokens itself (e.g. while
        // isLoading). Keep the cookie in step with the cache.
        if (isTokenNewer(tokens.access_token, getAccessTokenCookie())) {
          setAuthCookies({ accessToken: tokens.access_token });
        }

        minExpiresIn = Math.min(tokens.expires_in, getExpiryFromIdToken(tokens.id_token));
      }

      // A token already inside the grace window yields a negative delay, which fires on the next tick.
      // That is the intended "refresh now" path — do not clamp it to a positive floor.
      const delaySecs = error ? RETRY_DELAY_SECS : minExpiresIn - EAGER_EXPIRY_SECS;
      const timerHandle = setTimeout(refreshTimer, delaySecs * 1000);
      return timerHandle;
    })();

    promise.catch((err) => {
      setError(err);
    });

    return () => {
      promise.then(
        (timerHandle) => {
          if (timerHandle) {
            clearTimeout(timerHandle);
          }
        },
        () => {
          // Rejections are reported via promise.catch above; without this
          // handler the derived promise would emit an unhandled rejection.
        }
      );
    };
  }, [auth0, documentVisible, error, lastRefresh, online, getCachedTokens, refreshTimer]);

  return <>{children}</>;
};
